// ─── AgentsContext — état global du chat agents (story 5.5) ──────────────────
// Monté dans App.tsx AU-DESSUS du SidebarProvider (branche authentifiée) :
// la conversation survit à la navigation, et est réinitialisée au logout.
// Consommateurs : ChatView (5.5), AgentsCTA dynamique (5.6), ChatPanel (9.1).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useChatStream } from '@/hooks/useChatStream'
import type { ChatStreamFinishInfo, ChatStreamSettledInfo } from '@/hooks/useChatStream'
import {
  apiCreateConversation,
  apiGetConversationMessages,
  apiListConversations,
} from '@/lib/api'
import { mapPersistedMessages } from '@/lib/chatMapping'
import { AGENT_SUGGESTION_POOL, MAX_SUGGESTIONS, dedupeSuggestions, pickSuggestions } from '@/lib/agentSuggestions'
import {
  CHAT_MESSAGE_MAX_LENGTH,
  isToolResultPayload,
  type ActionCardData,
  type ActionCardState,
  type AgentChatMessage,
  type ChatConversationCreated,
  type ChatConversationSummary,
  type ChatStreamStatus,
} from '@/lib/chatTypes'

export type AgentsContextValue = {
  conversations: ChatConversationSummary[]
  conversationsLoading: boolean
  conversationsError: string | null
  activeConversationId: string | null
  messages: AgentChatMessage[]
  messagesLoading: boolean
  messagesError: string | null
  status: ChatStreamStatus
  error: string | null
  suggestions: string[]
  isStreaming: boolean
  sendMessage: (text: string) => Promise<void>
  stop: () => void
  selectConversation: (id: string) => Promise<void>
  startNewConversation: () => void
  refreshConversations: () => Promise<void>
  // Story 7.1 : `patch` optionnel (resultText, errorText, undoSecondsLeft…).
  setActionCardState: (
    messageId: string,
    cardId: string,
    state: ActionCardState,
    patch?: Partial<ActionCardData>
  ) => void
  // Story 7.1 (PO Q3) — lien d'une carte exécutée vers une vue de l'app.
  navigateTo: (route: string) => void
}

const AgentsContext = createContext<AgentsContextValue | undefined>(undefined)

export type AgentsProviderProps = {
  children: ReactNode
  // Navigation déclenchée par le tool `navigateTo` (événements live uniquement).
  onNavigate?: (route: string) => void
}

function errorText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

function extractRoute(result: unknown): string | null {
  if (!isToolResultPayload(result) || !result.success) return null
  const data = result.data
  if (typeof data !== 'object' || data === null) return null
  const route = (data as { route?: unknown }).route
  return typeof route === 'string' ? route : null
}

export function AgentsProvider({ children, onNavigate }: AgentsProviderProps) {
  const [conversations, setConversations] = useState<ChatConversationSummary[]>([])
  const [conversationsLoading, setConversationsLoading] = useState(true)
  const [conversationsError, setConversationsError] = useState<string | null>(null)
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messagesError, setMessagesError] = useState<string | null>(null)
  const [localSuggestions, setLocalSuggestions] = useState<string[]>(() =>
    pickSuggestions(AGENT_SUGGESTION_POOL)
  )

  const activeIdRef = useRef<string | null>(null)
  // Story 7.3 : pills affichées, envoyées avec chaque message (jamais re-proposées).
  const localSuggestionsRef = useRef<string[]>(localSuggestions)
  useEffect(() => {
    localSuggestionsRef.current = localSuggestions
  })
  const onNavigateRef = useRef(onNavigate)
  useEffect(() => {
    onNavigateRef.current = onNavigate
  })
  // Garde contre les réponses d'historique obsolètes (clics rapides).
  const historyRequestRef = useRef(0)
  // Création de conversation en cours (partagée entre envois rapprochés).
  const pendingCreateRef = useRef<Promise<ChatConversationCreated> | null>(null)

  const refreshConversations = useCallback(async () => {
    try {
      const list = await apiListConversations()
      setConversations(list)
      setConversationsError(null)
    } catch (e: unknown) {
      setConversationsError(errorText(e, 'Impossible de charger les conversations'))
    } finally {
      setConversationsLoading(false)
    }
  }, [])

  const rotateSuggestions = useCallback(() => {
    setLocalSuggestions((prev) => pickSuggestions(AGENT_SUGGESTION_POOL, prev))
  }, [])

  const handleToolResult = useCallback((name: string, result: unknown) => {
    if (name !== 'navigateTo') return
    const route = extractRoute(result)
    if (route) onNavigateRef.current?.(route)
  }, [])

  const handleFinish = useCallback(
    (info: ChatStreamFinishInfo) => {
      const fromBackend = dedupeSuggestions(info.suggestions ?? []).slice(0, MAX_SUGGESTIONS)
      if (fromBackend.length > 0) {
        setLocalSuggestions(fromBackend)
      } else {
        rotateSuggestions()
      }
    },
    [rotateSuggestions]
  )

  // Fin de CHAQUE envoi (DONE, stop, flux coupé, erreur) : la liste est
  // rafraîchie (conversation paresseuse visible, titre / updatedAt) ; hors
  // DONE, les pills tournent quand même.
  const handleSettled = useCallback(
    (info: ChatStreamSettledInfo) => {
      if (!info.completed) rotateSuggestions()
      void refreshConversations()
    },
    [refreshConversations, rotateSuggestions]
  )

  const {
    messages,
    setMessages,
    status,
    error,
    sendMessage: streamSend,
    stop,
  } = useChatStream({
    onToolResult: handleToolResult,
    onFinish: handleFinish,
    onSettled: handleSettled,
  })

  // Chargement initial de la liste (IIFE, pattern projet).
  useEffect(() => {
    void (async () => {
      await refreshConversations()
    })()
  }, [refreshConversations])

  const sendMessage = useCallback(
    async (text: string) => {
      const content = text.trim()
      if (!content || content.length > CHAT_MESSAGE_MAX_LENGTH) return
      setMessagesError(null)
      const existingId = activeIdRef.current
      // Génération courante : toute sélection / nouvelle conversation l'incrémente.
      const generation = historyRequestRef.current
      // Création paresseuse : jamais de conversation vide à l'ouverture de la vue.
      const conversationId = existingId
        ? existingId
        : async () => {
            // Une seule création en vol : deux envois rapides partagent la même
            // promesse (jamais deux conversations pour un même fil).
            let pending = pendingCreateRef.current
            if (!pending) {
              const request = apiCreateConversation()
              pending = request
              pendingCreateRef.current = request
              const clear = () => {
                if (pendingCreateRef.current === request) pendingCreateRef.current = null
              }
              request.then(clear, clear)
            }
            const created = await pending
            // Résolveur périmé : l'utilisateur a changé de conversation (ou en a
            // démarré une nouvelle) pendant la création → on n'écrase rien. Le
            // flux, avorté par stop(), ne partira pas.
            if (activeIdRef.current === null && historyRequestRef.current === generation) {
              activeIdRef.current = created.id
              setActiveConversationId(created.id)
            }
            return created.id
          }
      await streamSend(content, { conversationId, previousSuggestions: localSuggestionsRef.current })
    },
    [streamSend]
  )

  const selectConversation = useCallback(
    async (id: string) => {
      stop()
      activeIdRef.current = id
      setActiveConversationId(id)
      setMessages([])
      setMessagesError(null)
      setMessagesLoading(true)
      const requestId = ++historyRequestRef.current
      try {
        const rows = await apiGetConversationMessages(id)
        if (requestId !== historyRequestRef.current) return
        setMessages(mapPersistedMessages(rows))
      } catch (e: unknown) {
        if (requestId !== historyRequestRef.current) return
        setMessagesError(errorText(e, 'Impossible de charger la conversation'))
      } finally {
        if (requestId === historyRequestRef.current) setMessagesLoading(false)
      }
    },
    [stop, setMessages]
  )

  const startNewConversation = useCallback(() => {
    stop()
    historyRequestRef.current++
    activeIdRef.current = null
    setActiveConversationId(null)
    setMessages([])
    setMessagesError(null)
    setMessagesLoading(false)
    rotateSuggestions()
  }, [stop, setMessages, rotateSuggestions])

  const setActionCardState = useCallback(
    (messageId: string, cardId: string, state: ActionCardState, patch?: Partial<ActionCardData>) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id !== messageId || !m.actionCards
            ? m
            : {
                ...m,
                actionCards: m.actionCards.map((c) => (c.id === cardId ? { ...c, ...patch, state } : c)),
              }
        )
      )
    },
    [setMessages]
  )

  // Story 7.1 (PO Q3) — navigation depuis le lien d'une carte exécutée
  // (ex. createCampaign → vue Campagnes), même canal que le tool `navigateTo`.
  const navigateTo = useCallback((route: string) => {
    onNavigateRef.current?.(route)
  }, [])

  const isStreaming = status === 'submitted' || status === 'streaming'

  const value = useMemo<AgentsContextValue>(
    () => ({
      conversations,
      conversationsLoading,
      conversationsError,
      activeConversationId,
      messages,
      messagesLoading,
      messagesError,
      status,
      error,
      suggestions: localSuggestions,
      isStreaming,
      sendMessage,
      stop,
      selectConversation,
      startNewConversation,
      refreshConversations,
      setActionCardState,
      navigateTo,
    }),
    [
      conversations,
      conversationsLoading,
      conversationsError,
      activeConversationId,
      messages,
      messagesLoading,
      messagesError,
      status,
      error,
      localSuggestions,
      isStreaming,
      sendMessage,
      stop,
      selectConversation,
      startNewConversation,
      refreshConversations,
      setActionCardState,
      navigateTo,
    ]
  )

  return <AgentsContext.Provider value={value}>{children}</AgentsContext.Provider>
}

export function useAgents(): AgentsContextValue {
  const ctx = useContext(AgentsContext)
  if (!ctx) throw new Error('useAgents doit être utilisé à l’intérieur de <AgentsProvider>')
  return ctx
}
